import React from 'react';
import {
  Box, Spinner, Text, ScrollView,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useRoute, RouteProp } from '@react-navigation/native';
import type { LecturerRouteParamList } from '@/common/navigation/RoleNavigations/Lecturer/types';
import type { Task } from '@/common/api/models/task';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import ModuleMetaForm from './ModuleMetaForm/ModuleMetaForm';

interface ModuleMetaEditorProps {
  readonly?: boolean;
  autogradeIsReadOnly?: boolean;
  formIsDirty?: boolean;
  tasks?: Task[];
}

/**
 * ModuleMetaEditor - streamlined module settings form.
 * Clean section-based layout without nested card wrappers.
 */
const ModuleMetaEditor: React.FC<ModuleMetaEditorProps> = ({
  readonly = false,
  autogradeIsReadOnly = false,
  formIsDirty = false,
  tasks,
}) => {
  const { t } = useTranslation();
  const route = useRoute<RouteProp<LecturerRouteParamList, 'ModuleEditor'>>();
  const moduleId = route.params?.moduleId;
  const version = route.params?.version ?? '0.0';

  const { module, isModuleLoading } = useLecturerModuleData(moduleId, version, undefined);

  if (isModuleLoading) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center" bg="$bgApp">
        <Spinner size="large" />
      </Box>
    );
  }

  if (!module) {
    return null;
  }

  const isDraft = module.state?.workflowState === 'draft';
  const workflowState = module.state?.workflowState;

  return (
    <ScrollView flex={1} bg="$bgApp">
      <Box py="$4" px="$6" maxWidth={1280}>
        <Text size="xs" fontWeight="bold" color="$textSubtle" mb="$4" textTransform="uppercase" letterSpacing="$md">
          {t('lecturer.moduleEditor.moduleEditor.settingsDescription', {
            defaultValue: 'Module Settings',
          })}
        </Text>
        <ModuleMetaForm
          readonly={readonly}
          autogradeIsReadOnly={autogradeIsReadOnly}
          workingMode={module.metadata?.workingMode}
          moduleId={module.id}
          version={module.version ?? version}
          isDraft={isDraft}
          workflowState={workflowState}
          formIsDirty={formIsDirty}
          tasks={tasks}
        />
      </Box>
    </ScrollView>
  );
};

export default ModuleMetaEditor;
